import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  timeout: 30000, // 30s default; per-request overrides below for uploads
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track consecutive 401 errors to prevent logout on transient errors
let consecutive401Count = 0;
const MAX_CONSECUTIVE_401 = 2;

// Handle 401 errors - DEFENSIVE: Don't logout on first 401
api.interceptors.response.use(
  (response) => {
    // Reset counter on successful response
    consecutive401Count = 0;
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      const errorDetail = error.response?.data?.detail || '';
      
      // Only logout if token is explicitly invalid/expired
      // NOT on transient network errors or server issues
      const isTokenInvalid = 
        errorDetail === 'Token expired' ||
        errorDetail === 'Invalid token' ||
        errorDetail === 'Not authenticated' ||
        errorDetail === 'Could not validate credentials';
      
      if (isTokenInvalid) {
        consecutive401Count++;
        console.warn(`Auth 401 (${consecutive401Count}/${MAX_CONSECUTIVE_401}): ${errorDetail}`);
        
        // Only force logout after multiple consecutive auth failures
        // This prevents logout on transient errors
        if (consecutive401Count >= MAX_CONSECUTIVE_401) {
          console.error('Multiple auth failures - forcing logout');
          localStorage.removeItem('token');
          // Use replace to prevent back button issues
          window.location.replace('/login');
        }
      } else {
        // Non-auth 401 (rate limit, etc.) - don't count toward logout
        console.warn('Non-auth 401 error:', errorDetail);
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  getMe: () => api.get('/auth/me'),
  updatePassword: (data) => api.put('/auth/password', data),
  deleteAccount: () => api.delete('/auth/account'),
};

export const episodesAPI = {
  upload: (formData, onProgress) => api.post('/episodes/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 600000,
    onUploadProgress: onProgress,
  }),
  // Chunked upload for large files (bypasses proxy body size limits)
  uploadChunkedInit: (metadata) => api.post('/episodes/upload/init', metadata),
  uploadChunkedPart: (formData) => api.post('/episodes/upload/chunk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  }),
  uploadChunkedComplete: (data) => api.post('/episodes/upload/complete', data),
  getAll: () => api.get('/episodes'),
  getOne: (id) => api.get(`/episodes/${id}`),
  delete: (id) => api.delete(`/episodes/${id}`),
  retry: (id) => api.post(`/episodes/${id}/retry`),
  // Phase 1: ZIP Export
  exportZip: (id) => api.get(`/episodes/${id}/export/zip`, { responseType: 'blob' }),
  // Phase 2: AI Generation features
  generateBestQuotes: (id) => api.post(`/episodes/${id}/generate/best-quotes`),
  generateSeoTitles: (id) => api.post(`/episodes/${id}/generate/seo-titles`),
  generateTiktokCaptions: (id) => api.post(`/episodes/${id}/generate/tiktok-captions`),
  generateInstagramCaptions: (id) => api.post(`/episodes/${id}/generate/instagram-captions`),
  // YouTube outputs
  generateYoutubeDescription: (id) => api.post(`/episodes/${id}/generate/youtube-description`),
  generateYoutubeChapters: (id) => api.post(`/episodes/${id}/generate/youtube-chapters`),
  generateYoutubePinnedComment: (id) => api.post(`/episodes/${id}/generate/youtube-pinned-comment`),
  generateYoutubeShortsHooks: (id) => api.post(`/episodes/${id}/generate/youtube-shorts-hooks`),
  generateYoutubeSrt: (id) => api.post(`/episodes/${id}/generate/youtube-srt`),
  generateYoutubeSeoTitles: (id) => api.post(`/episodes/${id}/generate/youtube-seo-titles`),
  downloadSrt: (id) => api.get(`/episodes/${id}/download-srt`, { responseType: 'blob' }),
  // Task 2: Regenerate removed - these endpoints now return existing assets or error
  getRegenStatus: (id) => api.get(`/episodes/${id}/regen-status`),  // Deprecated, returns zeros
};

export const configAPI = {
  getTonePresets: () => api.get('/config/tone-presets'),
  getPlanFeatures: () => api.get('/config/plan-features'),
};

export const paymentsAPI = {
  createPayPalOrder: (plan) => api.post('/payments/paypal/create-order', { plan }),
  capturePayPalOrder: (orderId) => api.post('/payments/paypal/capture-order', { order_id: orderId }),
  getOrderStatus: (orderId) => api.get(`/payments/paypal/order/${orderId}`),
  getSubscriptionStatus: () => api.get('/payments/subscription-status'),
  getPlans: () => api.get('/plans'),
};

export const adminAPI = {
  getOverview: () => api.get('/admin/overview').then(res => res.data),
  getUsers: (params) => api.get('/admin/users', { params }).then(res => res.data),
  getUserDetails: (userId) => api.get(`/admin/users/${userId}`).then(res => res.data),
  blockUser: (userId) => api.post(`/admin/users/${userId}/block`).then(res => res.data),
  updateUserPlan: (userId, plan) => api.put(`/admin/users/${userId}/plan`, { plan }).then(res => res.data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`).then(res => res.data),
  getMonthlyAnalytics: (months = 12) => api.get(`/admin/analytics/monthly?months=${months}`).then(res => res.data),
  
  // Reconciliation endpoints
  checkUserStatus: (email) => api.get(`/admin/reconcile/check-user/${encodeURIComponent(email)}`).then(res => res.data),
  syncUserFromLemon: (email) => api.post(`/admin/reconcile/sync-user/${encodeURIComponent(email)}`).then(res => res.data),
  manualUpdateStatus: (email, data) => api.post(`/admin/reconcile/manual-update/${encodeURIComponent(email)}`, data).then(res => res.data),
  reconcileAllUsers: () => api.post('/admin/reconcile/all-paid-users').then(res => res.data),
  getSchedulerStatus: () => api.get('/admin/cron/scheduler-status').then(res => res.data),
  runSubscriptionCheck: () => api.post('/admin/cron/run-now').then(res => res.data),
  
  // Cost analytics
  getCostReport: () => api.get('/admin/cost/report').then(res => res.data),
  getEpisodeCost: (episodeId) => api.get(`/admin/cost/episode/${episodeId}`).then(res => res.data),
};

export default api;
